import { useCallback, useEffect, useRef, useState } from 'react';
import { useScreenRefresh } from '../../../shared/ui/hooks-screen/useScreenRefresh';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { fetchRemoteCashActivity } from '../../../reports/infra/reportsRemoteRepo'
import { invalidateReportsCache } from '../../../reports/infra/reportsRemoteCache';
import type { CashActivityItem } from '../../../reports/infra/reportsRemoteRepo';
import { isOnline } from '../../../shell/connectivity/connectivityInfra';

const PAGE_SIZE = 40;

type Params = {
  branchId: string | null;
  enabled: boolean;
};

export function useCashActivityFeed({ branchId, enabled }: Params) {
  const [items, setItems] = useState<CashActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [partialOffline, setPartialOffline] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const offsetRef = useRef(0);
  const loadedRef = useRef(false);

  const loadPage = useCallback(
    async (opts: { reset: boolean; forceRemote?: boolean }) => {
      if (!branchId || !enabled) {
        setItems([]);
        setHasMore(false);
        setPartialOffline(false);
        setFetchError(false);
        return;
      }

      const offset = opts.reset ? 0 : offsetRef.current;
      const since = new Date(Date.now() - 30 * 86400000).toISOString();
      const online = await isOnline();

      if (online) {
        try {
          if (opts.forceRemote) {
            invalidateReportsCache(`reports_branch_cash_activity|${branchId}|`);
          }
          const remote = await fetchRemoteCashActivity(branchId, {
            since,
            limit: PAGE_SIZE,
            offset,
          });
          if (remote) {
            setPartialOffline(false);
            setFetchError(false);
            setItems((prev) => (opts.reset ? remote.items : [...prev, ...remote.items]));
            offsetRef.current = offset + remote.items.length;
            setHasMore(remote.items.length === PAGE_SIZE);
            return;
          }
        } catch (error) {
          captureCatch('cash', 'activity-feed-remote-fetch')(error);
        }
        // Online pero falló el fetch: error explícito, conservar snapshot.
        setHasMore(false);
        setPartialOffline(false);
        setFetchError(true);
        return;
      }

      // ADR-017: sin SQLite — offline = vista parcial con lo ya cargado.
      setHasMore(false);
      setFetchError(false);
      setPartialOffline(true);
    },
    [branchId, enabled]
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await loadPage({ reset: true });
      loadedRef.current = true;
    } finally {
      setLoading(false);
    }
  }, [loadPage]);

  useEffect(() => {
    loadedRef.current = false;
    offsetRef.current = 0;
    setItems([]);
    setHasMore(false);
    setPartialOffline(false);
    setFetchError(false);
  }, [branchId, enabled]);

  const ensureLoaded = useCallback(async () => {
    if (!branchId || !enabled || loadedRef.current) return;
    await reload();
  }, [branchId, enabled, reload]);

  const { refreshing, onRefresh } = useScreenRefresh(async () => {
    await loadPage({ reset: true, forceRemote: true });
    loadedRef.current = true;
  });

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      await loadPage({ reset: false });
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadPage, loadingMore]);

  return {
    items,
    loading,
    refreshing,
    onRefresh,
    loadingMore,
    hasMore,
    loadMore,
    reload,
    ensureLoaded,
    partialOffline,
    fetchError,
  };
}
